import React, { useState } from 'react';
import GoogleSignInButton from '../components/common/GoogleSignInButton';
import FormInput from '../components/common/FormInput';
import PasswordInput from '../components/common/PasswordInput';
import DataInsights from '../components/illustrations/DataInsights';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';

const SignUp = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { signup, loginWithGoogle } = useAuth();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      return;
    }

    // Password validation
    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match!');
      return;
    }

    // Password strength validation (basic)
    if (formData.password.length < 8) {
      alert('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    try {
      const result = await signup(formData.email, formData.password, formData.fullName);
      
      if (result.success) {
        // Check if email verification is required
        if (!result.user?.email_confirmed) {
          navigate('/verify-email');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Sign up error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setLoading(true);
    try {
      await loginWithGoogle();
      // Google OAuth will redirect, so no need to navigate here
    } catch (error) {
      console.error('Google sign up error:', error);
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen font-sans">
      {/* Left side - Form */}
      <div className="w-full md:w-3/5 lg:w-2/3 flex items-center justify-center px-4 py-8 sm:px-6 sm:py-10 md:px-8 md:py-12 bg-white relative">
        <div className="w-full max-w-sm sm:max-w-md">
          {/* Welcome text */}
          <h2 className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
            Sign up
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
            Your journey to fun data collection starts here.
          </p>

          {/* Google Sign Up */}
          <GoogleSignInButton 
            text="Sign up with Google"
            onClick={handleGoogleSignUp}
            disabled={loading}
          />

          {/* Divider */}
          <div className="flex items-center my-5 sm:my-6 gap-4">
            <div className="flex-1 h-px bg-gray-300" />
            <span className="text-xs sm:text-sm text-gray-500">
              Or sign up with email
            </span>
            <div className="flex-1 h-px bg-gray-300" />
          </div>

          {/* Sign Up Form */}
          <form onSubmit={handleSubmit}>
  

            <FormInput
              label="Email"
              type="email"
              name="email"
              placeholder="Your email..."
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading}
            />

            <PasswordInput
              label="Password"
              name="password"
              placeholder="Your password..."
              value={formData.password}
              onChange={handleChange}
              required
              disabled={loading}
            />

            <PasswordInput
              label="Confirm Password"
              name="confirmPassword"
              placeholder="Confirm password..."
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              disabled={loading}
            />


            {/* Sign Up Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full px-4 py-3 sm:py-4 rounded-xl text-sm sm:text-base font-semibold mt-2 transition-all duration-300
                ${loading
                  ? 'bg-gray-400 cursor-not-allowed opacity-60'
                  : 'bg-blue-600 hover:bg-blue-700 hover:-translate-y-0.5 active:translate-y-0'
                } text-white`}
            >
              {loading ? 'CREATING ACCOUNT...' : 'Sign up with email'}
            </button>
          </form>

          {/* Sign In Link */}
          <p className="text-center mt-5 sm:mt-6 text-sm sm:text-base text-gray-700">
            Already have an account?{' '}
            <a
              onClick={() => !loading && navigate('/signin')}
              className={`text-blue-600 font-semibold no-underline ${loading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:text-blue-700'}`}
            >
              Sign In
            </a>
          </p>
        </div>
      </div>

      {/* Right side - Illustration */}
      <div className="hidden md:flex md:w-2/5 lg:w-1/3 relative">
        <DataInsights variant="signup" />
      </div>
    </div>
  );
};

export default SignUp;